/**
 * Sentry Client Configuration
 *
 * This file configures Sentry for the browser/client-side.
 * https://docs.sentry.io/platforms/javascript/guides/nextjs/
 */
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:id/refer\",\"regex\":\"^/([^/]+)/refer$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/forms/:id/edit\",\"regex\":\"^/forms/([^/]+)/edit$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/forms/:id/import-submissions\",\"regex\":\"^/forms/([^/]+)/import-submissions$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/forms/:id/responses\",\"regex\":\"^/forms/([^/]+)/responses$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/interview/:token\",\"regex\":\"^/interview/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/interview-feedback/:token\",\"regex\":\"^/interview-feedback/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/offer-templates/:id\",\"regex\":\"^/offer-templates/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/workspaces/:handle/edit\",\"regex\":\"^/workspaces/([^/]+)/edit$\",\"paramNames\":[\"handle\"],\"hasOptionalPrefix\":false},{\"path\":\"/:id\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/assignment/:token\",\"regex\":\"^/assignment/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/form/:id\",\"regex\":\"^/form/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/forms/:id\",\"regex\":\"^/forms/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/offer/:token\",\"regex\":\"^/offer/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/schedule/:token\",\"regex\":\"^/schedule/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/template/:id\",\"regex\":\"^/template/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/embed/:id\",\"regex\":\"^/embed/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/google-oauth/error\"},{\"path\":\"/login\"},{\"path\":\"/oauth/consent\"},{\"path\":\"/\"},{\"path\":\"/ai\"},{\"path\":\"/analytics\"},{\"path\":\"/applications\"},{\"path\":\"/candidates\"},{\"path\":\"/create\"},{\"path\":\"/discover/admin\"},{\"path\":\"/form-playground\"},{\"path\":\"/home\"},{\"path\":\"/import-form\"},{\"path\":\"/messages\"},{\"path\":\"/plans\"},{\"path\":\"/refer\"},{\"path\":\"/sentry-error-testing\"},{\"path\":\"/settings\"},{\"path\":\"/settings/billing\"},{\"path\":\"/workspace-ops\"},{\"path\":\"/ai-ats\"},{\"path\":\"/changelog\"},{\"path\":\"/compare\"},{\"path\":\"/compare/binary-vs-ashby\"},{\"path\":\"/compare/binary-vs-bamboohr\"},{\"path\":\"/compare/binary-vs-breezy\"},{\"path\":\"/compare/binary-vs-dover\"},{\"path\":\"/compare/binary-vs-freshteam\"},{\"path\":\"/compare/binary-vs-gem\"},{\"path\":\"/compare/binary-vs-greenhouse\"},{\"path\":\"/compare/binary-vs-homerun\"},{\"path\":\"/compare/binary-vs-jazzhr\"},{\"path\":\"/compare/binary-vs-keka\"},{\"path\":\"/compare/binary-vs-lever\"},{\"path\":\"/compare/binary-vs-recruit-crm\"},{\"path\":\"/compare/binary-vs-recruitee\"},{\"path\":\"/compare/binary-vs-rippling\"},{\"path\":\"/compare/binary-vs-teamtailor\"},{\"path\":\"/compare/binary-vs-workable\"},{\"path\":\"/compare/binary-vs-zoho-recruit\"},{\"path\":\"/features/ai-screening\"},{\"path\":\"/free-ats\"},{\"path\":\"/mcp\"},{\"path\":\"/resources/ashby-alternatives\"},{\"path\":\"/resources/ats-with-mcp\"},{\"path\":\"/resources/best-free-ats\"},{\"path\":\"/resources/breezy-alternatives\"},{\"path\":\"/resources/dover-alternatives\"},{\"path\":\"/resources/freshteam-alternatives\"},{\"path\":\"/resources/gem-alternatives\"},{\"path\":\"/resources/greenhouse-alternatives\"},{\"path\":\"/resources/keka-alternatives\"},{\"path\":\"/resources/lever-alternatives\"},{\"path\":\"/resources/recruitee-alternatives\"},{\"path\":\"/resources/screen-google-forms\"},{\"path\":\"/resources/screen-job-emails\"},{\"path\":\"/resources/teamtailor-alternatives\"},{\"path\":\"/resources/workable-alternatives\"},{\"path\":\"/resources/zoho-recruit-alternatives\"},{\"path\":\"/switch\"},{\"path\":\"/discover\"},{\"path\":\"/private/media\"},{\"path\":\"/edit/changelog\"},{\"path\":\"/gdpr\"},{\"path\":\"/landing\"},{\"path\":\"/payment/cancel\"},{\"path\":\"/payment/success\"},{\"path\":\"/pricing\"},{\"path\":\"/privacy\"},{\"path\":\"/terms\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.2.10";import * as Sentry from '@sentry/nextjs';

import {
	ALLOWED_CONSOLE_LEVELS,
	BROWSER_EXTENSION_PATTERNS,
	CRITICAL_HTTP_STATUS_CODES,
	ERROR_CATEGORIES,
	ERROR_FINGERPRINTS,
	ERROR_SEVERITY,
	IGNORED_BREADCRUMB_URLS,
	PERFORMANCE_THRESHOLDS,
	REPLAY_SAMPLE_RATES,
	SENTRY_DENY_URLS,
	SENTRY_IGNORE_ERRORS,
	TRACE_SAMPLE_RATES,
} from '@/shared/constants/sentry-constants';
import { shouldSendEvent } from '@/shared/utils/sentry-rate-limit';

Sentry.init({
	debug: false,
	dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
	release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
	environment: process.env.NEXT_PUBLIC_APP_ENV === 'production' ? 'production' : 'development',

	initialScope: {
		tags: { app_type: 'frontend' },
	},

	integrations: [
		Sentry.browserTracingIntegration({ enableInp: true }),
	],

	// Controls what percentage of performance transactions are sent to Sentry
	// Higher = more data but higher cost, Lower = less data but cheaper
	tracesSampler: (samplingContext) => {
		try {
			const name = samplingContext?.transactionContext?.name || '';

			// Always capture error-related transactions
			if (name.includes('error')) return TRACE_SAMPLE_RATES.ALWAYS;

			// Always capture slow page transactions
			if (name.includes('slow')) return TRACE_SAMPLE_RATES.ALWAYS;

			// Page navigations
			if (name.startsWith('/')) return TRACE_SAMPLE_RATES.PAGE_NAVIGATION;

			// Everything else (API calls, etc.)
			return TRACE_SAMPLE_RATES.DEFAULT;
		} catch {
			return TRACE_SAMPLE_RATES.DEFAULT;
		}
	},

	replaysSessionSampleRate: REPLAY_SAMPLE_RATES.SESSION,
	replaysOnErrorSampleRate: REPLAY_SAMPLE_RATES.ERROR_SESSION,

	ignoreErrors: SENTRY_IGNORE_ERRORS,
	denyUrls: SENTRY_DENY_URLS,

	beforeSend(event, hint) {
		const error = hint?.originalException;

		// Skip browser extension errors
		if (event.exception?.values?.[0]?.stacktrace?.frames) {
			const frames = event.exception.values[0].stacktrace.frames;
			const hasIgnoredSource = frames.some((frame) => {
				const filename = frame.filename || '';
				return BROWSER_EXTENSION_PATTERNS.some((pattern) => filename.includes(pattern));
			});
			if (hasIgnoredSource) return null;
		}

		// Tag chunk load errors
		if (error instanceof Error && error.name === 'ChunkLoadError') {
			event.tags = { ...event.tags, error_category: ERROR_CATEGORIES.CHUNK_LOAD };
			event.fingerprint = [ERROR_FINGERPRINTS.CHUNK_LOAD];
			event.level = ERROR_SEVERITY.MEDIUM;
		}

		// Tag hydration errors
		if (error instanceof Error && error.message?.includes('Hydration')) {
			event.tags = { ...event.tags, error_category: ERROR_CATEGORIES.HYDRATION };
			event.fingerprint = [ERROR_FINGERPRINTS.HYDRATION];
			event.level = ERROR_SEVERITY.MEDIUM;
		}

		// Add performance context
		if (typeof window !== 'undefined' && window.performance) {
			const navigation = performance.getEntriesByType(
				'navigation'
			)[0] as PerformanceNavigationTiming;
			if (navigation) {
				event.contexts = {
					...event.contexts,
					performance: {
						pageLoadTime: Math.round(navigation.loadEventEnd - navigation.startTime),
						domContentLoaded: Math.round(
							navigation.domContentLoadedEventEnd - navigation.startTime
						),
						ttfb: Math.round(navigation.responseStart - navigation.requestStart),
					},
				};
			}
		}

		// Last gate: bound a flood without ever hiding a first occurrence.
		if (!shouldSendEvent(event)) return null;

		return event;
	},

	beforeBreadcrumb(breadcrumb) {
		// Filter console logs - keep only warnings and errors
		if (breadcrumb.category === 'console') {
			if (!ALLOWED_CONSOLE_LEVELS.includes(breadcrumb.level as 'warn' | 'error')) return null;
		}

		// Filter XHR breadcrumbs - skip analytics/tracking scripts
		if (breadcrumb.category === 'xhr') {
			const url = breadcrumb.data?.url || '';
			if (IGNORED_BREADCRUMB_URLS.some((pattern) => url.includes(pattern))) {
				return null;
			}
		}

		return breadcrumb;
	},

	beforeSendTransaction(event) {
		const pageLoadTime = event.measurements?.['lcp']?.value;
		if (pageLoadTime) {
			if (pageLoadTime > PERFORMANCE_THRESHOLDS.PAGE_LOAD_CRITICAL_MS) {
				event.tags = { ...event.tags, performance_alert: 'critical', lcp_status: 'critical' };
			} else if (pageLoadTime > PERFORMANCE_THRESHOLDS.LCP_ALERT_MS) {
				event.tags = { ...event.tags, performance_alert: 'warning', lcp_status: 'poor' };
			}
		}

		const cls = event.measurements?.['cls']?.value;
		if (cls && cls > PERFORMANCE_THRESHOLDS.CLS_ALERT) {
			event.tags = { ...event.tags, cls_status: 'poor' };
		}

		return event;
	},
});

// Replay (rrweb) is off the critical path: fetched from Sentry's CDN after the page has
// loaded, then attached with the same options. Errors before that point have no replay.
if (typeof window !== 'undefined') {
	const addReplay = () => {
		Sentry.lazyLoadIntegration('replayIntegration')
			.then((replayIntegration) =>
				Sentry.addIntegration(replayIntegration({ maskAllText: false, blockAllMedia: true }))
			)
			.catch(() => {});
	};
	if (document.readyState === 'complete') addReplay();
	else window.addEventListener('load', addReplay, { once: true });
}

// Global error handlers - catch unhandled 5xx errors that slip through
if (typeof window !== 'undefined') {
	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason;
		try {
			if (reason?.response?.status) {
				const status = reason.response.status;
				if (CRITICAL_HTTP_STATUS_CODES.includes(status)) {
					Sentry.withScope((scope) => {
						scope.setLevel(ERROR_SEVERITY.CRITICAL);
						scope.setTag('error_category', ERROR_CATEGORIES.UNHANDLED_5XX);
						scope.setTag('http_status', status);
						Sentry.captureException(reason);
					});
				}
			}
		} catch (error) {
			console.error('Error handling unhandled rejection', { event, error });
		}
	});
}
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
